import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { portalAuthService } from '../services/portalApi';

const PortalAuthContext = createContext(null);

const initialState = {
  isAuthenticated: false,
  user: null,
  loading: true,
  error: null,
};

function portalAuthReducer(state, action) {
  switch (action.type) {
    case 'LOGIN_SUCCESS':
      return { ...state, isAuthenticated: true, user: action.payload, loading: false, error: null };
    case 'LOGOUT':
      return { ...state, isAuthenticated: false, user: null, loading: false, error: null };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'INIT_COMPLETE':
      return { ...state, loading: false };
    default:
      return state;
  }
}

export function PortalAuthProvider({ children }) {
  const [state, dispatch] = useReducer(portalAuthReducer, initialState);

  // Check if user is already authenticated on mount
  useEffect(() => {
    let cancelled = false;

    async function checkAuth() {
      try {
        const response = await portalAuthService.getMe();
        if (!cancelled) {
          dispatch({ type: 'LOGIN_SUCCESS', payload: response.data });
        }
      } catch {
        if (!cancelled) {
          dispatch({ type: 'INIT_COMPLETE' });
        }
      }
    }

    checkAuth();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (credentials) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      await portalAuthService.login(credentials);
      const meResponse = await portalAuthService.getMe();
      dispatch({ type: 'LOGIN_SUCCESS', payload: meResponse.data });
      return meResponse.data;
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      dispatch({ type: 'SET_ERROR', payload: message });
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await portalAuthService.logout();
    } catch {
      // Ignore logout errors
    }
    dispatch({ type: 'LOGOUT' });
  }, []);

  const value = {
    ...state,
    login,
    logout,
  };

  return (
    <PortalAuthContext.Provider value={value}>
      {children}
    </PortalAuthContext.Provider>
  );
}

export function usePortalAuth() {
  const context = useContext(PortalAuthContext);
  if (!context) {
    throw new Error('usePortalAuth must be used within a PortalAuthProvider');
  }
  return context;
}

export default PortalAuthContext;
